import { ValidationErrorData } from '../../../typings/common';
import { GroupId } from '../../../typings/groups';
import { EditGroupData } from '../../../typings/groupsEditState';
import {
    ClientErrorsConfigRule,
    EditGroupFieldErrorsData,
    EditGroupValidationParams
} from '../ClientGroupEditErrors/ClientGroupEditErrors';

/**
 * @class
 * @description Абстрактная валидационная схема для формы
 *
 * @method validate
 * @method getClientGroupEditErrors
 * @method getClientErrorsConfig
 */
export abstract class GroupValidationSchema {
    /**
     * Валидирует значения формы, возвращает объект с ошибками
     *
     * @param groups    Значения
     * @param params    Параметры валидации
     * @returns         Объект с ошибками
     */
    public validate(
        groups: Array<EditGroupData>,
        params: EditGroupValidationParams
    ): Record<GroupId, Record<string, EditGroupFieldErrorsData>> {
        return groups.reduce((res, group: EditGroupData) => {
            const groupValidationResult = this.getErrors(group, params);

            if (Object.keys(groupValidationResult).length !== 0) {
                res = {
                    ...res,
                    [group.id]: groupValidationResult
                };
            }

            return res;
        }, {});
    }

    /**
     *
     * @param group
     * @param params
     */
    protected getErrors(
        group: EditGroupData,
        params: EditGroupValidationParams
    ): Record<string, EditGroupFieldErrorsData> {
        const clientValidationConfig = this.getClientErrorsConfig(params);

        return Object.keys(clientValidationConfig).reduce(
            (result, fieldName) => {
                const fieldErrors = this.getFieldValidationErrors(
                    group,
                    fieldName,
                    params
                );

                if (fieldErrors.length !== 0) {
                    result = {
                        ...result,
                        [fieldName]: {
                            errors: fieldErrors
                        }
                    };
                }

                return result;
            },
            {}
        );
    }

    protected getFieldValidationErrors(
        group: EditGroupData,
        fieldName: string,
        params: EditGroupValidationParams
    ): Array<ValidationErrorData> {
        const clientValidationConfig = this.getClientErrorsConfig(params);

        return clientValidationConfig[fieldName].rules.reduce(
            (
                errors: Array<ValidationErrorData>,
                rule: ClientErrorsConfigRule<'name'>
            ) => {
                const fieldValue = group.value[fieldName];

                if (
                    (rule.needToValidate === undefined ||
                        rule.needToValidate(group.value, fieldValue)) &&
                    !rule.validate(fieldValue)
                ) {
                    errors.push({
                        path: `groups[${group.id}].${fieldName}`,
                        code: rule.code,
                        text: rule.text
                    });
                }

                return errors;
            },
            []
        );
    }

    protected abstract getClientErrorsConfig(
        params: EditGroupValidationParams
    ): Partial<object>;
}
